import { useEffect, useState } from 'react'
import toastService from '@/services/toast/service'
import { Loader2, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { xrayAdminService, type AdminXrayPlan, type StripePriceInfo } from '@/services/xrayAdmin'
import { formatMoney } from '@/lib/xray-pricing'

interface XrayPlanEditModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  plan: AdminXrayPlan | null
  onSaved: (updated: AdminXrayPlan) => void
}

interface Draft {
  displayPrice: string
  stripePriceId: string
  includedEnrichUnits: string
}

const draftFromPlan = (plan: AdminXrayPlan | null): Draft => ({
  displayPrice: plan?.displayPrice == null ? '' : String(plan.displayPrice),
  stripePriceId: plan?.stripePriceId ?? '',
  includedEnrichUnits: String(plan?.includedEnrichUnits ?? 0),
})

export default function XrayPlanEditModal({ open, onOpenChange, plan, onSaved }: XrayPlanEditModalProps) {
  const [draft, setDraft] = useState<Draft>(draftFromPlan(plan))
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<StripePriceInfo | null>(null)
  const [testError, setTestError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setDraft(draftFromPlan(plan))
      setTestResult(null)
      setTestError(null)
    }
  }, [open, plan])

  // Clear test result when the user edits the Stripe Price ID
  useEffect(() => {
    setTestResult(null)
    setTestError(null)
  }, [draft.stripePriceId])

  const handleTestStripe = async () => {
    const priceId = draft.stripePriceId.trim()
    if (!priceId || testing) return
    setTesting(true)
    setTestError(null)
    try {
      const info = await xrayAdminService.testStripePrice(priceId)
      setTestResult(info)
    } catch (err: any) {
      setTestError(err?.response?.data?.message || err?.message || 'Failed to fetch Stripe price.')
      setTestResult(null)
    } finally {
      setTesting(false)
    }
  }

  if (!plan) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return

    // The price is not sent: the backend pulls the live amount from Stripe with
    // this ID and derives both the display price and the monthly fee from it.
    // The allowance IS sent - it is a product decision Stripe knows nothing about.
    const units = Number(draft.includedEnrichUnits)
    if (!Number.isInteger(units) || units < 0) {
      toastService.error('Free Enriches must be a whole number, 0 or more')
      return
    }
    const body: Partial<Omit<AdminXrayPlan, 'id' | 'name'>> = {
      stripePriceId: draft.stripePriceId.trim() || null,
      includedEnrichUnits: units,
    }

    setIsSubmitting(true)
    try {
      const updated = await xrayAdminService.updatePlan(plan.id, body)
      onSaved(updated)
      toastService.success(`"${updated.displayName}" saved`)
      onOpenChange(false)
    } catch (err: any) {
      toastService.error(err.response?.data?.message || "Couldn't update plan")
    } finally {
      setIsSubmitting(false)
    }
  }

  // The monthly fee follows the Stripe price, so a tested price previews the
  // new fee before the admin saves.
  const depositPreviewCents = testResult?.unitAmount ?? plan.monthlyFeeCents

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit {plan.displayName}</DialogTitle>
            <DialogDescription>
              Point the plan at its Stripe price; the monthly fee is pulled from Stripe on save.
              The fee is not credited to anyone's balance - it buys the free Enriches below.
              Per-lead prices live on the pricing grid.
            </DialogDescription>
          </DialogHeader>

          <DialogBody className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="plan-display-name">Plan name</Label>
              <Input
                id="plan-display-name"
                value={plan.displayName}
                disabled
                readOnly
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="plan-price">
                  Monthly price <span className="text-muted-foreground">($)</span>
                </Label>
                <Input
                  id="plan-price"
                  type="number"
                  value={
                    testResult?.unitAmount != null
                      ? String(Math.round(testResult.unitAmount / 100))
                      : draft.displayPrice
                  }
                  disabled
                  readOnly
                  placeholder="From Stripe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan-included">
                  Free Xray Enriches <span className="text-muted-foreground">/ mo</span>
                </Label>
                <Input
                  id="plan-included"
                  type="number"
                  min={0}
                  step={1}
                  value={draft.includedEnrichUnits}
                  onChange={(e) => setDraft((d) => ({ ...d, includedEnrichUnits: e.target.value }))}
                  disabled={isSubmitting}
                  className="tabular-nums"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              The {formatMoney(depositPreviewCents)} monthly fee is charged, not credited: it buys the
              free Enriches above. Everything else comes out of the customer&apos;s wallet balance.
              Unused free Enriches expire at the end of each billing period.
            </p>

            <div className="space-y-2">
              <Label htmlFor="plan-stripe-id">Stripe Price ID</Label>
              <div className="flex gap-2">
                <Input
                  id="plan-stripe-id"
                  value={draft.stripePriceId}
                  onChange={(e) => setDraft((d) => ({ ...d, stripePriceId: e.target.value }))}
                  disabled={isSubmitting}
                  placeholder="price_..."
                  className="font-mono text-xs"
                />
                {(
                  <Button
                    type="button"
                    variant="outline"
                    size="default"
                    onClick={handleTestStripe}
                    disabled={!draft.stripePriceId.trim() || testing || isSubmitting}
                    className="shrink-0"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                        Testing...
                      </>
                    ) : (
                      'Test'
                    )}
                  </Button>
                )}
              </div>

              {testResult && (
                <StripeTestResultPanel result={testResult} />
              )}

              {testError && (
                <div className="rounded-md border border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20 px-3 py-2.5 text-xs flex items-start gap-2">
                  <XCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400 mt-px" />
                  <div className="text-red-700 dark:text-red-300">{testError}</div>
                </div>
              )}

              {!testResult && !testError && (
                <p className="text-xs text-muted-foreground">
                  Paste the Stripe Price ID — on save we pull the amount from Stripe and use it as the displayed
                  price. Use <strong>Test</strong> to preview it first. Existing subscribers are unaffected until
                  they re-subscribe.
                </p>
              )}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =========================================================================
// Stripe test result panel
// =========================================================================

function StripeTestResultPanel({
  result,
}: {
  result: StripePriceInfo
}) {
  const stripeDollars =
    result.unitAmount != null ? `$${(result.unitAmount / 100).toFixed(2)}` : '—'

  // Verdict logic
  const isInactive = !result.active
  const isOneTime = result.type !== 'recurring'
  const isWrongInterval = result.interval !== null && result.interval !== 'month'
  const nonUsdCurrency = result.currency !== 'usd'

  const warnings: { tone: 'warn' | 'error'; text: string }[] = []
  if (isInactive) warnings.push({ tone: 'error', text: 'This Stripe price is archived (inactive) — new subscriptions will fail.' })
  if (isOneTime) warnings.push({ tone: 'error', text: 'This is a one-time price, not recurring — X-Ray plans must be recurring.' })
  if (isWrongInterval) warnings.push({ tone: 'warn', text: `Billing interval is "${result.interval}", expected "month".` })
  if (nonUsdCurrency) warnings.push({ tone: 'warn', text: `Currency is ${result.currency.toUpperCase()}, expected USD.` })

  const allClear = warnings.length === 0

  return (
    <div
      className={`rounded-md border px-3 py-2.5 text-xs space-y-2 ${
        allClear
          ? 'bg-muted/40'
          : warnings.some((w) => w.tone === 'error')
            ? 'border-red-200 dark:border-red-900/40 bg-red-50/50 dark:bg-red-950/20'
            : 'border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-950/20'
      }`}
    >
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Stripe says
      </div>
      <dl className="flex flex-col gap-1">
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Amount</dt>
          <dd className="font-semibold tabular-nums">
            {stripeDollars} {result.currency.toUpperCase()}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Billing</dt>
          <dd className="font-semibold tabular-nums">
            {result.type === 'recurring' && result.interval
              ? `every ${result.intervalCount && result.intervalCount > 1 ? result.intervalCount + ' ' : ''}${result.interval}${result.intervalCount && result.intervalCount > 1 ? 's' : ''}`
              : 'one-time'}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="text-muted-foreground">Status</dt>
          <dd className="font-semibold">{result.active ? 'Active' : 'Archived'}</dd>
        </div>
        {result.product && (
          <div className="flex justify-between gap-3">
            <dt className="text-muted-foreground">Product</dt>
            <dd className="font-semibold truncate text-right max-w-[200px]">{result.product.name}</dd>
          </div>
        )}
      </dl>

      <div className="pt-1 border-t border-current/10 space-y-1">
        {allClear ? (
          <div className="inline-flex items-start gap-1.5 text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0 mt-px" />
            <span>Everything checks out — ready to save.</span>
          </div>
        ) : (
          warnings.map((w, i) => (
            <div
              key={i}
              className={`inline-flex items-start gap-1.5 ${
                w.tone === 'error'
                  ? 'text-red-700 dark:text-red-400'
                  : 'text-amber-700 dark:text-amber-400'
              }`}
            >
              {w.tone === 'error' ? (
                <XCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
              )}
              <span>{w.text}</span>
            </div>
          ))
        )}
      </div>
    </div>
  )
}
